import { useEffect, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Bell } from "lucide-react";

/**
 * Displays the admin notice as a modal on every fresh login while the notice
 * is ACTIVE. AuthContext.login() clears the dismissal flag so the popup
 * re-appears on every login. Within a single login-session it stays dismissed
 * for the currently posted notice; if admin edits (updated_at changes) or
 * re-activates the notice, the popup re-triggers automatically.
 * Skipped for admin users so admins don't repeatedly see their own notice.
 */
export default function NoticePopup() {
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const [notice, setNotice] = useState({ active: false, message: "", updated_by: "", updated_at: "" });

  useEffect(() => {
    if (!user) return;
    // Skip admins — they wrote it
    if (user.role === "admin") return;
    api.get("/settings/notice").then(({ data }) => {
      if (!data?.active || !(data?.message || "").trim()) return;
      // Dismissal key includes updated_at so a fresh notice always re-shows,
      // even if the previous one was dismissed in this session.
      const uid = user.id || user.email;
      const key = `notice_dismissed_${uid}_${data.updated_at || ""}`;
      if (sessionStorage.getItem(key)) return;
      setNotice(data);
      setOpen(true);
    }).catch(() => {});
  }, [user]);

  const close = () => {
    const uid = user?.id || user?.email;
    const key = `notice_dismissed_${uid}_${notice.updated_at || ""}`;
    sessionStorage.setItem(key, "1");
    setOpen(false);
  };

  return (
    <Dialog open={open} onOpenChange={(v) => v ? null : close()}>
      <DialogContent
        className="bg-white max-w-lg w-[calc(100vw-2rem)] sm:w-full"
        data-testid="notice-popup"
        onPointerDownOutside={(e) => e.preventDefault()}
        onEscapeKeyDown={(e) => e.preventDefault()}
      >
        <DialogHeader>
          <div className="flex items-center gap-2">
            <span className="h-10 w-10 rounded-full bg-red-100 flex items-center justify-center animate-pulse">
              <Bell className="h-5 w-5 text-red-600" />
            </span>
            <div>
              <DialogTitle className="text-red-700 font-display text-xl tracking-tight">
                Important Notice
              </DialogTitle>
              <div className="text-[11px] text-slate-500 mt-0.5">
                Posted by {notice.updated_by || "admin"} · {(notice.updated_at || "").slice(0, 16).replace("T", " ")}
              </div>
            </div>
          </div>
        </DialogHeader>
        <div className="rounded-md border border-red-200 bg-gradient-to-br from-red-50 to-white px-4 py-4 text-sm text-slate-800 whitespace-pre-wrap leading-relaxed" data-testid="notice-message">
          {notice.message}
        </div>
        <DialogFooter>
          <Button onClick={close} className="bg-red-600 hover:bg-red-700 text-white font-semibold" data-testid="notice-close">
            I Understand — Continue
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
